/**
 * JE Protect operator interface.
 *
 * A token system rather than ad-hoc values, so spacing and type stay consistent
 * as screens are added and a theme change is one block rather than a search.
 *
 * Both schemes are defined properly this time. The earlier bug was declaring
 * `color-scheme: light dark` while shipping a fixed light palette — the browser
 * then styled form controls for the OS scheme while the page stayed light, and
 * button labels came out white on white. Every surface and every control below
 * has an explicit colour in both schemes, because controls do not inherit one.
 */

:root {
  /* Type scale */
  --text-xs: 0.75rem;
  --text-sm: 0.8125rem;
  --text-base: 0.9375rem;
  --text-lg: 1.0625rem;
  --text-xl: 1.375rem;

  /* Spacing scale, 4px base */
  --s1: 0.25rem;
  --s2: 0.5rem;
  --s3: 0.75rem;
  --s4: 1rem;
  --s5: 1.5rem;
  --s6: 2rem;
  --s7: 3rem;

  --radius: 8px;
  --radius-lg: 12px;

  /* Light theme */
  --bg: #f6f7f9;
  --surface: #ffffff;
  --surface-2: #f9fafb;
  --fg: #111827;
  --fg-muted: #6b7280;
  --fg-subtle: #9ca3af;
  --line: #e5e7eb;
  --line-strong: #d1d5db;
  --sidebar: #ffffff;

  --accent: #1d4ed8;
  --accent-fg: #ffffff;
  --accent-soft: #eff6ff;

  --ok: #15803d;
  --ok-soft: #f0fdf4;
  --warn: #b45309;
  --warn-soft: #fffbeb;
  --danger: #b91c1c;
  --danger-soft: #fef2f2;

  --shadow: 0 1px 2px rgb(16 24 40 / 6%), 0 1px 3px rgb(16 24 40 / 4%);
  --shadow-lg: 0 4px 12px rgb(16 24 40 / 8%);

  color-scheme: light;
}

@media (prefers-color-scheme: dark) {
  :root {
    --bg: #0b0e13;
    --surface: #151a22;
    --surface-2: #1b212b;
    --sidebar: #10141b;
    --fg: #e6e8eb;
    --fg-muted: #9aa4b2;
    --fg-subtle: #6b7684;
    --line: #2a313c;
    --line-strong: #3a4350;

    --accent: #4d82f3;
    --accent-fg: #0e1116;
    --accent-soft: #16233d;

    --ok: #4ade80;
    --ok-soft: #10231a;
    --warn: #fbbf24;
    --warn-soft: #241d0e;
    --danger: #f87171;
    --danger-soft: #2a1518;

    --shadow: none;
    --shadow-lg: none;

    color-scheme: dark;
  }
}

* { box-sizing: border-box; }

body {
  margin: 0;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: var(--text-base);
  line-height: 1.5;
  color: var(--fg);
  background: var(--bg);
  -webkit-font-smoothing: antialiased;
}

h1, h2, h3 { margin: 0; font-weight: 600; letter-spacing: -0.01em; }
h1 { font-size: var(--text-xl); }
h2 { font-size: var(--text-lg); }
h3 { font-size: var(--text-base); }
p { margin: 0; }

/* ---------------------------------------------------------------- shell */

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s4);
  padding: var(--s3) var(--s5);
  background: var(--surface);
  border-bottom: 1px solid var(--line);
}

.brand { display: flex; align-items: center; gap: var(--s2); font-weight: 650; letter-spacing: -0.02em; }
.brand__mark {
  width: 1.5rem; height: 1.5rem; border-radius: 6px;
  background: var(--accent); color: var(--accent-fg);
  display: grid; place-items: center; font-size: var(--text-xs); font-weight: 700;
}
.topbar__right { display: flex; align-items: center; gap: var(--s3); }
.topbar__user { color: var(--fg-muted); font-size: var(--text-sm); }

.container { max-width: 68rem; margin: 0 auto; padding: var(--s6) var(--s5) var(--s7); }

.section { margin-bottom: var(--s6); }
.section__head {
  display: flex; align-items: baseline; justify-content: space-between;
  gap: var(--s4); margin-bottom: var(--s4); flex-wrap: wrap;
}
.section__meta { color: var(--fg-muted); font-size: var(--text-sm); }

/* --------------------------------------------------------------- buttons */
/* Every button sets colour AND background. Controls do not inherit colour, and
   leaving it to the user agent is what made labels invisible before. */

.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: var(--s2);
  padding: 0.5rem 0.875rem;
  font: inherit; font-size: var(--text-sm); font-weight: 500;
  color: var(--fg); background: var(--surface);
  border: 1px solid var(--line-strong); border-radius: var(--radius);
  cursor: pointer; white-space: nowrap;
  transition: background 120ms ease, border-color 120ms ease;
}
.btn:hover { background: var(--surface-2); border-color: var(--fg-subtle); }
.btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.btn:disabled { color: var(--fg-subtle); background: var(--surface-2); cursor: default; }

.btn--primary { color: var(--accent-fg); background: var(--accent); border-color: var(--accent); }
.btn--primary:hover { filter: brightness(1.08); background: var(--accent); }
.btn--primary:disabled { filter: none; opacity: 0.55; color: var(--accent-fg); background: var(--accent); }

.btn--danger { color: var(--danger); border-color: var(--line-strong); background: var(--surface); }
.btn--danger:hover { background: var(--danger-soft); border-color: var(--danger); }

.btn--sm { padding: 0.3rem 0.6rem; font-size: var(--text-xs); }
.btn--block { width: 100%; }

.link {
  background: none; border: none; padding: 0;
  font: inherit; font-size: var(--text-sm);
  color: var(--accent); cursor: pointer; text-decoration: none;
}
.link:hover { text-decoration: underline; }

/* ----------------------------------------------------------------- cards */

.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
}
.card__body { padding: var(--s4); }
.card__foot {
  padding: var(--s3) var(--s4);
  border-top: 1px solid var(--line);
  background: var(--surface-2);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  display: flex; gap: var(--s2); flex-wrap: wrap; align-items: center;
}

.grid { display: grid; gap: var(--s4); grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr)); }

/* --------------------------------------------------------------- device */

.device__top { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--s3); }
.device__name { font-size: var(--text-base); }
.device__site { color: var(--fg-muted); font-size: var(--text-sm); margin-top: 2px; }
.device__seen { color: var(--fg-subtle); font-size: var(--text-xs); margin-top: var(--s1); }

/* A dot reads faster than a word at a glance across a fleet. */
.status { display: inline-flex; align-items: center; gap: var(--s2); font-size: var(--text-xs); font-weight: 500; white-space: nowrap; }
.status__dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.status--online { color: var(--ok); }
.status--online .status__dot { background: var(--ok); box-shadow: 0 0 0 3px color-mix(in srgb, var(--ok) 18%, transparent); }
.status--offline { color: var(--warn); }
.status--offline .status__dot { background: var(--warn); }
.status--never-connected { color: var(--fg-subtle); }
.status--never-connected .status__dot { background: var(--fg-subtle); }

.pill {
  display: inline-block; padding: 2px var(--s2); border-radius: 999px;
  font-size: var(--text-xs); font-weight: 500;
  color: var(--warn); background: var(--warn-soft);
  border: 1px solid color-mix(in srgb, var(--warn) 35%, transparent);
}

.cameras { list-style: none; margin: var(--s3) 0 0; padding: 0; }
.cameras__summary { color: var(--fg-muted); font-size: var(--text-sm); margin-top: var(--s3); }
.camera {
  display: flex; align-items: center; justify-content: space-between; gap: var(--s2);
  padding: var(--s2) 0; border-top: 1px solid var(--line); font-size: var(--text-sm);
}
.camera__name { color: var(--fg); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.camera__state { color: var(--fg-muted); font-size: var(--text-xs); }
.camera--streaming .camera__state { color: var(--ok); }
.camera--error .camera__state, .camera--offline .camera__state { color: var(--danger); }

.stale { margin-top: var(--s3); font-size: var(--text-xs); color: var(--warn); }

/* ----------------------------------------------------------------- forms */

.form { display: grid; gap: var(--s3); }
.field { display: grid; gap: var(--s1); }
.field > label { font-size: var(--text-sm); font-weight: 500; color: var(--fg); }
.field__hint { font-size: var(--text-xs); color: var(--fg-subtle); }

input, select, textarea {
  width: 100%;
  padding: 0.5rem 0.625rem;
  font: inherit; font-size: var(--text-sm);
  /* Explicit in both schemes, for the same reason as the buttons. */
  color: var(--fg); background: var(--surface);
  border: 1px solid var(--line-strong); border-radius: var(--radius);
}
input::placeholder { color: var(--fg-subtle); }
input:focus-visible, select:focus-visible {
  outline: 2px solid var(--accent); outline-offset: -1px; border-color: var(--accent);
}
.field--invalid input { border-color: var(--danger); }
.field__error { font-size: var(--text-xs); color: var(--danger); }

.form__actions { display: flex; gap: var(--s2); margin-top: var(--s1); }
/* Explains a consequence rather than labelling a field, so it reads quieter than a label. */
.form__hint { font-size: var(--text-xs); color: var(--fg-muted); margin: 0; line-height: 1.5; }
.form__row { display: grid; gap: var(--s3); grid-template-columns: 1fr 1fr; }

/* --------------------------------------------------------------- notices */

.notice {
  padding: var(--s3); border-radius: var(--radius);
  font-size: var(--text-sm); border: 1px solid transparent;
}
.notice--info { color: var(--accent); background: var(--accent-soft); border-color: color-mix(in srgb, var(--accent) 25%, transparent); }
.notice--warn { color: var(--warn); background: var(--warn-soft); border-color: color-mix(in srgb, var(--warn) 30%, transparent); }
.notice--error { color: var(--danger); background: var(--danger-soft); border-color: color-mix(in srgb, var(--danger) 30%, transparent); }
.notice--ok { color: var(--ok); background: var(--ok-soft); border-color: color-mix(in srgb, var(--ok) 30%, transparent); }

.empty {
  padding: var(--s6); text-align: center;
  color: var(--fg-muted); font-size: var(--text-sm);
  border: 1px dashed var(--line-strong); border-radius: var(--radius-lg);
}
.empty__title { color: var(--fg); font-weight: 600; margin-bottom: var(--s1); }

/* ------------------------------------------------------------ claim code */
/* Deliberately the loudest thing on the page: this is the one moment the code
   exists anywhere an operator can see it. */

.claim {
  border: 1px solid var(--accent);
  border-radius: var(--radius-lg);
  background: var(--accent-soft);
  padding: var(--s4) var(--s5);
  margin-bottom: var(--s5);
  box-shadow: var(--shadow-lg);
}
.claim__label { font-size: var(--text-xs); font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: var(--accent); }
.claim__device { font-size: var(--text-sm); color: var(--fg-muted); margin-top: 2px; }
.claim__code {
  margin: var(--s3) 0 var(--s1);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: clamp(1.75rem, 6vw, 2.5rem);
  font-weight: 600; letter-spacing: 0.14em; color: var(--fg);
}
.claim__expiry { font-size: var(--text-sm); color: var(--fg-muted); }
.claim__expiry--expired { color: var(--danger); font-weight: 600; }
.claim__warning { margin-top: var(--s3); font-size: var(--text-xs); color: var(--fg-muted); max-width: 42rem; }
.claim__actions { display: flex; gap: var(--s2); margin-top: var(--s4); }

/* ----------------------------------------------------------------- login */

.login-page { min-height: 100vh; display: grid; place-items: center; padding: var(--s5); }
.login { width: 100%; max-width: 22rem; }
.login__head { text-align: center; margin-bottom: var(--s5); }
.login__sub { color: var(--fg-muted); font-size: var(--text-sm); margin-top: var(--s1); }

@media (max-width: 40rem) {
  .container { padding: var(--s4) var(--s3) var(--s6); }
  .topbar { padding: var(--s3); }
  .form__row { grid-template-columns: 1fr; }
  .grid { grid-template-columns: 1fr; }
}

/* ------------------------------------------------------------ app shell */
/*
 * A sidebar rather than a single scrolling column.
 *
 * The first build stacked clients, provisioning and devices on one page, which
 * put an occasional action (provisioning) permanently above the thing an
 * operator actually looks at (the fleet). Navigation separates "what I am doing
 * now" from "everything this tool can do".
 */

.shell { display: grid; grid-template-columns: 15rem 1fr; min-height: 100vh; }

.sidebar {
  background: var(--sidebar);
  border-right: 1px solid var(--line);
  display: flex; flex-direction: column;
  padding: var(--s4) var(--s3);
  gap: var(--s5);
  position: sticky; top: 0; height: 100vh;
}

.sidebar__brand { display: flex; align-items: center; gap: var(--s2); padding: 0 var(--s2); font-weight: 650; letter-spacing: -0.02em; }

.nav { display: flex; flex-direction: column; gap: 2px; }
.nav__item {
  display: flex; align-items: center; gap: var(--s3);
  padding: 0.5rem var(--s2); border-radius: var(--radius);
  font-size: var(--text-sm); font-weight: 500;
  color: var(--fg-muted); background: none; border: none;
  text-decoration: none; cursor: pointer; text-align: left; width: 100%;
}
.nav__item:hover { background: var(--surface-2); color: var(--fg); }
.nav__item--active { background: var(--accent-soft); color: var(--accent); }
.nav__item svg { width: 18px; height: 18px; flex: none; }
.nav__count { margin-left: auto; font-size: var(--text-xs); color: var(--fg-subtle); }

.sidebar__foot { margin-top: auto; display: grid; gap: var(--s2); padding: 0 var(--s2); }
.sidebar__user { font-size: var(--text-xs); color: var(--fg-subtle); overflow: hidden; text-overflow: ellipsis; }

.main { min-width: 0; display: flex; flex-direction: column; }

.page-head {
  display: flex; align-items: center; justify-content: space-between; gap: var(--s4);
  padding: var(--s4) var(--s6);
  border-bottom: 1px solid var(--line);
  background: var(--surface);
  position: sticky; top: 0; z-index: 5;
}
.page-head__title { display: grid; gap: 2px; }
.page-head__sub { font-size: var(--text-sm); color: var(--fg-muted); }
.page-head__actions { display: flex; gap: var(--s2); }

.page-body { padding: var(--s5) var(--s6) var(--s7); max-width: 80rem; }

/* --------------------------------------------------------------- metrics */

.metrics { display: grid; gap: var(--s4); grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr)); margin-bottom: var(--s6); }
.metric { padding: var(--s4); border: 1px solid var(--line); border-radius: var(--radius-lg); background: var(--surface); }
.metric__label { font-size: var(--text-xs); color: var(--fg-muted); text-transform: uppercase; letter-spacing: 0.05em; }
.metric__value { font-size: 1.75rem; font-weight: 600; letter-spacing: -0.02em; margin-top: var(--s1); }
.metric__hint { font-size: var(--text-xs); color: var(--fg-subtle); margin-top: 2px; }
.metric--attention .metric__value { color: var(--warn); }

/* ----------------------------------------------------------------- panel */
/* Provisioning lives behind a button now, not permanently on screen. */

.panel-backdrop {
  position: fixed; inset: 0; background: rgb(0 0 0 / 45%);
  display: grid; place-items: start center; padding: var(--s6) var(--s4);
  overflow-y: auto; z-index: 50;
}
.panel-card { width: 100%; max-width: 34rem; }
.panel-card__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: var(--s4); border-bottom: 1px solid var(--line);
}

.table { width: 100%; border-collapse: collapse; font-size: var(--text-sm); }
.table th {
  text-align: left; font-size: var(--text-xs); font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em; color: var(--fg-muted);
  padding: var(--s2) var(--s3); border-bottom: 1px solid var(--line);
}
.table td { padding: var(--s3); border-bottom: 1px solid var(--line); vertical-align: top; }
.table tr:last-child td { border-bottom: none; }
.table__actions { text-align: right; white-space: nowrap; }

@media (max-width: 56rem) {
  .shell { grid-template-columns: 1fr; }
  .sidebar {
    position: static; height: auto; flex-direction: row; align-items: center;
    gap: var(--s3); overflow-x: auto; border-right: none; border-bottom: 1px solid var(--line);
  }
  .nav { flex-direction: row; }
  .nav__count { display: none; }
  .sidebar__foot { margin-top: 0; margin-left: auto; }
  .sidebar__user { display: none; }
  .page-head, .page-body { padding-left: var(--s4); padding-right: var(--s4); }
}


/* ------------------------------------------------------- device detail */

.detail { display: grid; gap: var(--s5); }
.detail__back { margin: 0; font-size: var(--text-sm); }
.detail__back a { color: var(--fg-muted); text-decoration: none; }
.detail__back a:hover { color: var(--fg); }

.detail__head { display: flex; align-items: start; justify-content: space-between; gap: var(--s4); }
.detail__name { margin: 0; font-size: var(--text-xl); }
.detail__where { margin: var(--s1) 0 0; color: var(--fg-muted); font-size: var(--text-sm); }

/*
 * A definition list, because that is what this is: labelled facts about one
 * thing. Auto-fit rather than a fixed column count, so it stays readable in the
 * sidebar layout and on a phone without a breakpoint for each.
 */
.facts {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: var(--s4); margin: var(--s5) 0 0;
}
.fact dt { font-size: var(--text-xs); color: var(--fg-subtle); text-transform: uppercase; letter-spacing: 0.04em; }
.fact dd { margin: var(--s1) 0 0; font-size: var(--text-sm); color: var(--fg); }
.fact--warn { color: var(--warn); }

/* Monospace because it is compared character by character against a log line. */
.fingerprint {
  display: inline-block; margin-left: var(--s2);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: var(--text-xs);
  padding: 1px var(--s2); border-radius: var(--radius);
  background: var(--surface-2); border: 1px solid var(--line); color: var(--fg-muted);
}

.detail__cameras { display: grid; gap: var(--s3); }
.detail__cameras-head { display: flex; align-items: center; justify-content: space-between; gap: var(--s3); }
.detail__cameras-head h3 { margin: 0; font-size: var(--text-lg); }

.camera-rows { list-style: none; margin: 0; padding: 0; display: grid; gap: var(--s3); }
.camera-row {
  display: grid; gap: var(--s3);
  padding: var(--s4); border: 1px solid var(--line); border-radius: var(--radius-lg);
  background: var(--surface); box-shadow: var(--shadow);
}
.camera-row__main { display: flex; align-items: start; justify-content: space-between; gap: var(--s4); flex-wrap: wrap; }
.camera-row__ident { display: grid; gap: 2px; min-width: 0; }
.camera-row__name { font-weight: 500; overflow: hidden; text-overflow: ellipsis; }
.camera-row__addr { font-size: var(--text-xs); color: var(--fg-subtle); }
.camera-row__meta { display: flex; align-items: center; gap: var(--s3); flex-wrap: wrap; }
.camera-row__creds { font-size: var(--text-xs); color: var(--fg-muted); }
.camera-row__actions { display: flex; gap: var(--s2); flex-wrap: wrap; }
/* Indented and separated, so an open panel reads as belonging to its row. */
.camera-row__panel { padding-top: var(--s3); border-top: 1px solid var(--line); }

/* State colours on the pill, so a fleet scan does not require reading. */
.pill--streaming { color: var(--ok); background: var(--ok-soft); border-color: color-mix(in srgb, var(--ok) 35%, transparent); }
.pill--error, .pill--offline { color: var(--danger); background: var(--danger-soft); border-color: color-mix(in srgb, var(--danger) 35%, transparent); }
.pill--unknown, .pill--muted, .pill--connecting {
  color: var(--fg-muted); background: var(--surface-2);
  border-color: var(--line-strong);
}


/* --------------------------------------------------------------- people */

.user-rows { list-style: none; margin: var(--s4) 0 0; padding: 0; display: grid; gap: var(--s2); }
.user-row {
  display: grid; gap: var(--s3);
  padding: var(--s4); border: 1px solid var(--line); border-radius: var(--radius-lg);
  background: var(--surface); box-shadow: var(--shadow);
}
.user-row__ident { display: grid; gap: 2px; min-width: 0; }
.user-row__email { display: flex; align-items: center; gap: var(--s2); font-weight: 500; }
.user-row__activity { font-size: var(--text-xs); color: var(--fg-muted); }
.user-row__actions { display: flex; align-items: center; gap: var(--s2); flex-wrap: wrap; }
.user-row__role select { width: auto; min-width: 8rem; }
.user-row__note { font-size: var(--text-xs); color: var(--fg-subtle); }

/*
 * The invitation link. Wraps rather than truncating: a link that is visually cut
 * off invites someone to copy half of it by hand.
 */
.invite { margin-bottom: var(--s5); border-color: color-mix(in srgb, var(--accent) 35%, transparent); }
.invite__link {
  display: block; margin: var(--s3) 0;
  padding: var(--s3); border-radius: var(--radius);
  background: var(--surface-2); border: 1px solid var(--line);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: var(--text-xs);
  word-break: break-all; line-height: 1.6;
}

.entitlement-form { width: 100%; margin-top: var(--s3); }

/* ------------------------------------------------------------------ auth */

.auth { min-height: 100dvh; display: grid; place-items: center; padding: var(--s5); }
.auth__card { width: 100%; max-width: 24rem; }
.auth__title { font-size: var(--text-xl); margin-bottom: var(--s2); }

/* Present for screen readers, absent visually — used where a control's purpose
   is obvious in context but a label is still required. */
.visually-hidden {
  position: absolute; width: 1px; height: 1px;
  margin: -1px; padding: 0; overflow: hidden;
  clip-path: inset(50%); white-space: nowrap; border: 0;
}


/* --------------------------------------------------------- camera scanning */

.detail__cameras-actions { display: flex; gap: var(--s2); flex-wrap: wrap; }

/* Full width wherever it opens: it appears in a card footer and in the detail
   page's header row, and a flex parent would otherwise squeeze it. */
.scan {
  flex: 1 1 100%;
  display: grid; gap: var(--s3);
  padding: var(--s4); border: 1px solid var(--line); border-radius: var(--radius-lg);
  background: var(--surface); box-shadow: var(--shadow);
}
.scan__head { display: flex; align-items: center; justify-content: space-between; gap: var(--s3); flex-wrap: wrap; }
.scan__head h4 { margin: 0; font-size: var(--text-base); font-weight: 600; }

.scan__list { list-style: none; margin: 0; padding: 0; display: grid; gap: var(--s2); }
.scan__item {
  display: flex; align-items: center; justify-content: space-between; gap: var(--s3);
  padding: var(--s3); border: 1px solid var(--line); border-radius: var(--radius);
  background: var(--surface-2);
}
.scan__ident { display: grid; gap: 2px; min-width: 0; }
.scan__name { font-size: var(--text-sm); font-weight: 500; }
.scan__addr { font-size: var(--text-xs); color: var(--fg-subtle); }

.scan__panel { padding-top: var(--s3); border-top: 1px solid var(--line); }

/* Wraps rather than truncating: a URI cut off mid-path cannot be checked. */
.scan__uri {
  display: block; padding: var(--s2); border-radius: var(--radius);
  background: var(--surface-2); border: 1px solid var(--line);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: var(--text-xs);
  word-break: break-all; color: var(--fg-muted);
}

.field__label { font-size: var(--text-sm); font-weight: 500; color: var(--fg); }


/* ------------------------------------------------------- access editing */

.user-row__access { font-size: var(--text-xs); color: var(--accent); }

.access {
  grid-column: 1 / -1;
  display: grid; gap: var(--s3);
  padding: var(--s4); margin-top: var(--s3);
  border-top: 1px solid var(--line);
}
.access h4 { margin: 0; font-size: var(--text-sm); font-weight: 600; }

/* Radio and label on one line, hint beneath, so the choice reads as a sentence
   rather than a control with a caption somewhere near it. */
.access__choice { display: flex; gap: var(--s3); align-items: start; padding: var(--s2) 0; }
.access__choice input { width: auto; margin-top: 3px; flex: none; }
.access__choice > span { display: grid; gap: 2px; }

.access__sites { list-style: none; margin: 0; padding: 0; display: grid; gap: var(--s2); }
.access__site {
  padding: var(--s3); border: 1px solid var(--line); border-radius: var(--radius);
  background: var(--surface-2);
}
.access__row { display: flex; gap: var(--s3); align-items: center; }
.access__row input { width: auto; flex: none; }
.access__row--nested { padding-left: var(--s4); font-size: var(--text-sm); }
.access__client { display: block; font-size: var(--text-xs); color: var(--fg-subtle); }
.access__cameras {
  list-style: none; margin: var(--s2) 0 0; padding: 0 0 0 var(--s3);
  display: grid; gap: var(--s1); border-left: 2px solid var(--line);
}


/* ----------------------------------------------------------- audit log */

.audit__filters { display: flex; gap: var(--s2); flex-wrap: wrap; margin-bottom: var(--s4); }

.audit { list-style: none; margin: 0; padding: 0; display: grid; gap: var(--s1); }
.audit__row {
  display: grid; gap: 2px;
  padding: var(--s3); border: 1px solid var(--line); border-radius: var(--radius);
  background: var(--surface);
}
/* A left edge rather than a coloured background: the destructive rows are what
   someone scrolls to find, and a wash of red over half the page stops meaning
   anything. */
.audit__row--destructive { border-left: 3px solid var(--danger); }
.audit__summary { font-size: var(--text-sm); }
.audit__meta { font-size: var(--text-xs); color: var(--fg-subtle); }


.destroy {
  display: grid; gap: var(--s3);
  padding: var(--s4); margin-top: var(--s3);
  border: 1px solid var(--danger); border-radius: var(--radius-lg);
  background: var(--danger-soft);
}
.destroy h4 { margin: 0; font-size: var(--text-base); }
